import {get, isEqual} from 'lodash';
import {
  DEBUG_STORAGE_ROOT_KEY,
  getPaginationDebugFlags,
} from '../util/paginationTelemetry';
import {getBlacklineDebugFlags} from '../util/debugFlags';
import {useLocalStorage} from './useLocalStorage';

/**
 * Subscribe to debug flags sourced from localStorage.
 *
 * Why this exists:
 * - `storage` events do not fire for same-tab writes.
 * - multiple surfaces need the same flag sync behavior.
 * - callers should re-render only when flag values actually change.
 *
 * Usage:
 * - `useDebugFlags()` -> full boolean snapshot object
 * - `useDebugFlags('pagination.woAdjust')` -> selected boolean (default false)
 * - `useDebugFlags('blackline')` -> selected boolean (default false)
 *
 * Runtime decision path:
 * 1) Build a normalized snapshot (`{pagination: ..., ...}`) from telemetry helpers.
 * 2) Subscribe to storage updates for `_debug` key.
 * 3) Refresh on cross-tab `storage` events.
 * 4) Refresh on same-tab localStorage writes via storage notifier.
 * 5) Apply `isEqual` guard so React state updates only when values changed.
 *
 * @param {string} [path]
 * @returns {Record<string, unknown> | boolean}
 */
export const useDebugFlags = (path = null) => {
  const flags = useLocalStorage(
    DEBUG_STORAGE_ROOT_KEY,
    () => ({
      pagination: getPaginationDebugFlags(),
      ...getBlacklineDebugFlags(),
    }),
    {isEqual}
  );

  if (path) return Boolean(get(flags, path, false));
  return flags;
};
